/**
 * Fuzzy Search Utilities
 * Implements Levenshtein distance for typo-tolerant matching
 * with optional phonetic normalization for German names.
 * 
 * Examples:
 * - "Müler" matches "Müller" with distance 1
 * - "Shneider" matches "Schneider" via phonetic normalization
 * - "Mueller" matches "Müller" via phonetic normalization
 */

import { normalizePhonetic } from './phoneticNormalization';

/**
 * Calculate Levenshtein distance between two strings
 * Lower distance = more similar strings
 */
export function levenshteinDistance(a: string, b: string): number {
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  
  if (aLower === bLower) return 0;
  if (aLower.length === 0) return bLower.length;
  if (bLower.length === 0) return aLower.length;

  const matrix: number[][] = [];

  // Initialize first column
  for (let i = 0; i <= bLower.length; i++) {
    matrix[i] = [i];
  }

  // Initialize first row
  for (let j = 0; j <= aLower.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= bLower.length; i++) {
    for (let j = 1; j <= aLower.length; j++) {
      if (bLower.charAt(i - 1) === aLower.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[bLower.length][aLower.length];
}

/**
 * Check if search term fuzzy-matches target string
 * @param search - The search term
 * @param target - The target string to match against
 * @param maxDistance - Maximum allowed edit distance (default: adaptive based on length)
 */
export function isFuzzyMatch(
  search: string, 
  target: string, 
  maxDistance?: number
): boolean {
  if (!search || !target) return false;
  
  const searchLower = search.toLowerCase().trim();
  const targetLower = target.toLowerCase().trim();
  
  // Short terms: require exact or near-exact match
  if (searchLower.length <= 2) {
    return targetLower.includes(searchLower);
  }
  
  // Calculate adaptive threshold based on search term length
  // Shorter words allow fewer errors
  const adaptiveThreshold = maxDistance ?? Math.floor(searchLower.length / 3);
  
  // First check if it's a simple substring match (faster)
  if (targetLower.includes(searchLower)) {
    return true;
  }
  
  // For longer targets, check each word
  const targetWords = targetLower.split(/\s+/);
  for (const word of targetWords) {
    if (word.length >= searchLower.length - adaptiveThreshold) {
      const distance = levenshteinDistance(searchLower, word);
      if (distance <= adaptiveThreshold) {
        return true;
      }
    }
    
    // Also check if word starts with a fuzzy match of search
    if (word.startsWith(searchLower.substring(0, Math.max(2, searchLower.length - 1)))) {
      return true;
    }
  }
  
  return false;
}

/**
 * Find best fuzzy matches in a list of strings
 * Returns matches sorted by similarity (best first)
 */
export function findFuzzyMatches(
  search: string,
  targets: string[],
  maxResults: number = 10,
  maxDistance: number = 2
): Array<{ value: string; distance: number }> {
  if (!search || search.length < 2) return [];
  
  const searchLower = search.toLowerCase();
  const matches: Array<{ value: string; distance: number }> = [];
  
  for (const target of targets) {
    if (!target) continue;
    
    const targetLower = target.toLowerCase();
    
    // Exact substring match has distance 0
    if (targetLower.includes(searchLower)) {
      matches.push({ value: target, distance: 0 });
      continue;
    }
    
    // Check word-by-word
    const words = targetLower.split(/\s+/);
    let bestDistance = Infinity;
    
    for (const word of words) {
      const distance = levenshteinDistance(searchLower, word);
      if (distance < bestDistance && distance <= maxDistance) {
        bestDistance = distance;
      }
    }
    
    if (bestDistance <= maxDistance) {
      matches.push({ value: target, distance: bestDistance });
    }
  }
  
  // Sort by distance (lower = better match)
  return matches
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults);
}

/**
 * Enhanced multi-term fuzzy matching
 * All search terms must match (AND logic) with fuzzy tolerance
 */
export function matchesFuzzyTerms(
  searchTerms: string[],
  ...targetStrings: (string | undefined | null)[]
): boolean {
  const validTargets = targetStrings.filter(Boolean) as string[];
  if (validTargets.length === 0) return false;
  
  const combinedTarget = validTargets.join(' ');
  
  // All terms must fuzzy-match somewhere in the combined target
  return searchTerms.every(term => {
    if (term.length < 2) return true; // Skip very short terms
    return isFuzzyMatch(term, combinedTarget);
  });
}

// ============================================
// PHONETIC FUZZY MATCHING (German-optimized)
// ============================================

/**
 * Check if search term phonetically matches target string
 * Uses German phonetic normalization before Levenshtein comparison
 * 
 * This enables matching like:
 * - "Shneider" → "Schneider" 
 * - "Mueller" → "Müller"
 * - "Mair" → "Meier"
 * 
 * @param search - The search term
 * @param target - The target string to match against
 * @param maxDistance - Maximum allowed edit distance (default: adaptive based on normalized length)
 */
export function isPhoneticFuzzyMatch(
  search: string,
  target: string,
  maxDistance?: number
): boolean {
  if (!search || !target) return false;
  
  const searchLower = search.toLowerCase().trim();
  const targetLower = target.toLowerCase().trim();
  
  // First, try exact substring match (fastest path)
  if (targetLower.includes(searchLower)) {
    return true;
  }
  
  // Short terms: require exact or near-exact match
  if (searchLower.length <= 2) {
    return targetLower.includes(searchLower);
  }
  
  // Normalize both strings phonetically
  const normalizedSearch = normalizePhonetic(search);
  const normalizedTarget = normalizePhonetic(target);
  
  // If phonetic normalization produces same result, it's a match
  if (normalizedTarget.includes(normalizedSearch)) {
    return true;
  }
  
  // Calculate adaptive threshold based on NORMALIZED length
  // More generous than regular fuzzy: length / 2 instead of length / 3
  const adaptiveThreshold = maxDistance ?? Math.max(1, Math.floor(normalizedSearch.length / 2));
  
  // Check each word in the normalized target
  const targetWords = normalizedTarget.split(/\s+/);
  for (const word of targetWords) {
    if (word.length >= normalizedSearch.length - adaptiveThreshold) {
      const distance = levenshteinDistance(normalizedSearch, word);
      if (distance <= adaptiveThreshold) {
        return true;
      }
    }
    
    // Also check if word starts with search term
    if (word.startsWith(normalizedSearch.substring(0, Math.max(2, normalizedSearch.length - 1)))) {
      return true;
    }
  }
  
  // Final check: full string Levenshtein on normalized versions
  // This catches cases where word boundaries don't align perfectly
  const fullDistance = levenshteinDistance(normalizedSearch, normalizedTarget);
  if (fullDistance <= adaptiveThreshold) {
    return true;
  }
  
  return false;
}

/**
 * Phonetic multi-term fuzzy matching
 * All search terms must match (AND logic) with phonetic + fuzzy tolerance
 * 
 * This is the recommended function for German name/term search
 */
export function matchesPhoneticFuzzyTerms(
  searchTerms: string[],
  ...targetStrings: (string | undefined | null)[]
): boolean {
  const validTargets = targetStrings.filter(Boolean) as string[];
  if (validTargets.length === 0) return false;
  
  const combinedTarget = validTargets.join(' ');
  
  // All terms must phonetic-fuzzy-match somewhere in the combined target
  return searchTerms.every(term => {
    if (term.length < 2) return true; // Skip very short terms
    return isPhoneticFuzzyMatch(term, combinedTarget);
  });
}

/**
 * Find best phonetic fuzzy matches in a list of strings
 * Returns matches sorted by phonetic similarity (best first)
 */
export function findPhoneticFuzzyMatches(
  search: string,
  targets: string[],
  maxResults: number = 10,
  maxDistance: number = 3
): Array<{ value: string; distance: number; isPhoneticMatch: boolean }> {
  if (!search || search.length < 2) return [];
  
  const searchLower = search.toLowerCase();
  const normalizedSearch = normalizePhonetic(search);
  const matches: Array<{ value: string; distance: number; isPhoneticMatch: boolean }> = [];
  
  for (const target of targets) {
    if (!target) continue;
    
    const targetLower = target.toLowerCase();
    const normalizedTarget = normalizePhonetic(target);
    
    // Exact substring match = distance 0
    if (targetLower.includes(searchLower)) {
      matches.push({ value: target, distance: 0, isPhoneticMatch: false });
      continue;
    }
    
    // Phonetic exact match = distance 0.5 (slightly worse than exact)
    if (normalizedTarget.includes(normalizedSearch)) {
      matches.push({ value: target, distance: 0.5, isPhoneticMatch: true });
      continue;
    }
    
    // Phonetic Levenshtein distance
    const phoneticDistance = levenshteinDistance(normalizedSearch, normalizedTarget);
    if (phoneticDistance <= maxDistance) {
      matches.push({ value: target, distance: phoneticDistance, isPhoneticMatch: true });
    }
  }
  
  // Sort by distance (lower = better match)
  return matches
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults);
}
